import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';

const empty = () => ({ fromCompanyId: '', toCompanyId: '', amount: '', description: '' });

export function IntercompanyPostingModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState(null);

  const companies = useQuery({ queryKey: ['finance', 'company-codes'], queryFn: () => financeApi.listCompanyCodes().then((r) => r.items), enabled: open });

  useEffect(() => { if (open) { setForm(empty()); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: (body) => financeApi.createIntercompanyPosting(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['finance', 'intercompany'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.fromCompanyId || !form.toCompanyId || !form.amount || Number(form.amount) <= 0) { setError(t('ic.error.postingRequired')); return; }
    if (form.fromCompanyId === form.toCompanyId) { setError(t('ic.error.distinct')); return; }
    save.mutate({ fromCompanyId: form.fromCompanyId, toCompanyId: form.toCompanyId, amount: Number(form.amount), description: form.description || null });
  }

  const options = companies.data || [];

  return (
    <Modal open={open} onClose={onClose} title={t('ic.posting.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <div className="field-row">
          <label className="field"><span className="field-label">{t('ic.posting.from')}</span>
            <select className="input" value={form.fromCompanyId} onChange={set('fromCompanyId')} required>
              <option value="">-</option>
              {options.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
            </select>
          </label>
          <label className="field"><span className="field-label">{t('ic.posting.to')}</span>
            <select className="input" value={form.toCompanyId} onChange={set('toCompanyId')} required>
              <option value="">-</option>
              {options.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
            </select>
          </label>
        </div>
        <label className="field" style={{ maxWidth: 200 }}><span className="field-label">{t('ic.col.amount')}</span>
          <input className="input num" type="number" min="0.01" step="0.01" value={form.amount} onChange={set('amount')} required />
        </label>
        <label className="field"><span className="field-label">{t('ic.col.description')}</span>
          <input className="input" value={form.description} onChange={set('description')} />
        </label>
        {options.length < 2 && <div className="alert">{t('ic.posting.needTwo')}</div>}
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
